import React, { useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    InputAdornment,
    Divider,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Card,
    CardContent,
    Stack,
    Chip,
    LinearProgress,
    Button,
    IconButton,
    Slider,
    Paper,
    useTheme,
    alpha
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    TrendingUp as TrendingUpIcon,
    Calculate as CalculateIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    Science as ScienceIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { calculateDeviation } from './utils';

// Test types data defined directly here
const testTypes = [
    { value: 'pH', label: 'pH Level', unit: 'pH', category: 'Chemical' },
    { value: 'viscosity', label: 'Viscosity', unit: 'cP', category: 'Physical' },
    { value: 'density', label: 'Density/Specific Gravity', unit: 'g/ml', category: 'Physical' },
    { value: 'temperature', label: 'Temperature Control', unit: '°C', category: 'Physical' },
    { value: 'homogeneity', label: 'Homogeneity/Mixing Uniformity', unit: 'pass/fail', category: 'Physical' },
    { value: 'color', label: 'Color Consistency', unit: 'ΔE', category: 'Sensory' },
    { value: 'odor', label: 'Odor Profile', unit: 'scale', category: 'Sensory' },
    { value: 'appearance', label: 'Visual Inspection', unit: 'pass/fail', category: 'Sensory' },
    { value: 'moisture', label: 'Water Content', unit: '%', category: 'Chemical' },
    { value: 'particle_size', label: 'Particle Size Distribution', unit: 'μm', category: 'Physical' },
    { value: 'emulsion_stability', label: 'Emulsion Stability', unit: 'min', category: 'Physical' },
    { value: 'conductivity', label: 'Electrical Conductivity', unit: 'μS/cm', category: 'Chemical' }
];

export default function SpecificationsStep({ 
    formData, 
    setFormData, 
    onValidationChange,
    showSnackbar
}) {
    const theme = useTheme();
    const [validationErrors, setValidationErrors] = useState({});
    const [specificationComplete, setSpecificationComplete] = useState(false);
    const [rangeRecommendations, setRangeRecommendations] = useState([]);
    const [statisticalAnalysis, setStatisticalAnalysis] = useState({});

    // All tests are now pass/fail only
    const specificationType = 'pass_fail';

    // Simple validation for pass/fail only
    const validateSpecifications = () => {
        const errors = {};
        
        if (!formData.acceptance_criteria) {
            errors.acceptance_criteria = 'Acceptance criteria must be defined';
        }

        setValidationErrors(errors);
        const isValid = Object.keys(errors).length === 0;
        setSpecificationComplete(isValid);
        
        return isValid;
    };

    // Calculate statistical analysis
    const calculateStatistics = () => {
        const target = parseFloat(formData.target_value);
        const min = parseFloat(formData.min_acceptable);
        const max = parseFloat(formData.max_acceptable);

        if (!isNaN(target) && !isNaN(min) && !isNaN(max)) {
            const rangeWidth = max - min;
            const centerPoint = (min + max) / 2;
            const targetOffset = target - centerPoint;
            const tolerancePercent = ((rangeWidth / 2) / target) * 100;
            const asymmetry = targetOffset / (rangeWidth / 2);

            setStatisticalAnalysis({
                rangeWidth: rangeWidth.toFixed(3),
                centerPoint: centerPoint.toFixed(3),
                targetOffset: targetOffset.toFixed(3),
                tolerancePercent: tolerancePercent.toFixed(2),
                asymmetry: asymmetry.toFixed(3),
                isSymmetric: Math.abs(asymmetry) < 0.1
            });
        }
    };

    // Generate range recommendations based on test type
    const generateRecommendations = () => {
        const recommendations = [];
        
        if (formData.test_type) {
            switch (formData.test_type) {
                case 'pH':
                    recommendations.push({
                        type: 'range',
                        message: 'pH tolerance typically ±0.2-0.5 units for cosmetics',
                        suggested: { min: 6.0, max: 8.0, target: 7.0 }
                    });
                    break;
                case 'viscosity':
                    recommendations.push({
                        type: 'range',
                        message: 'Viscosity tolerance typically ±10-20% for liquids',
                        suggested: { min: 800, max: 1200, target: 1000 }
                    });
                    break;
                case 'density':
                    recommendations.push({
                        type: 'range',
                        message: 'Density tolerance typically ±0.02-0.05 g/ml',
                        suggested: { min: 0.98, max: 1.02, target: 1.00 }
                    });
                    break;
                case 'fill_weight':
                    recommendations.push({
                        type: 'range',
                        message: 'Fill weight tolerance typically ±2-5% for packaged goods',
                        suggested: { min: 95, max: 105, target: 100 }
                    });
                    break;
                default:
                    recommendations.push({
                        type: 'general',
                        message: 'Consider industry standards and process capability when setting ranges'
                    });
            }
        }

        setRangeRecommendations(recommendations);
    };

    // Notify parent of validation status when data changes
    useEffect(() => {
        const timer = setTimeout(() => {
            const isValid = validateSpecifications();
            calculateStatistics();
            if (onValidationChange) {
                onValidationChange('specifications', isValid);
            }
        }, 150);
        return () => clearTimeout(timer);
    }, [
        formData.acceptance_criteria
    ]);

    // Generate recommendations when test type changes
    useEffect(() => {
        if (formData.test_type) {
            generateRecommendations();
        }
    }, [formData.test_type]);

    const handleTargetValueChange = (value) => {
        setFormData(prev => ({...prev, target_value: value}));
    };

    const handleMinAcceptableChange = (value) => {
        setFormData(prev => ({...prev, min_acceptable: value}));
    };

    const handleMaxAcceptableChange = (value) => {
        setFormData(prev => ({...prev, max_acceptable: value}));
    };

    const handleTargetUnitChange = (value) => {
        setFormData(prev => ({...prev, target_unit: value}));
    };

    const applySuggestedRange = (suggested) => {
        setFormData(prev => ({
            ...prev,
            target_value: suggested.target.toString(),
            min_acceptable: suggested.min.toString(),
            max_acceptable: suggested.max.toString()
        }));
        showSnackbar?.('Applied suggested specification range', 'info');
    };

    const autoCalculateSymmetricRange = () => {
        const target = parseFloat(formData.target_value);
        if (!isNaN(target)) {
            const tolerance = target * 0.05; // 5% tolerance
            setFormData(prev => ({
                ...prev,
                min_acceptable: (target - tolerance).toFixed(2),
                max_acceptable: (target + tolerance).toFixed(2)
            }));
            showSnackbar?.('Applied symmetric 5% tolerance range', 'success');
        }
    };

    // Get specification completion percentage
    const getCompletionPercentage = () => {
        const requiredFields = ['acceptance_criteria'];
        const completedFields = requiredFields.filter(field => formData[field]);
        return requiredFields.length > 0 ? (completedFields.length / requiredFields.length) * 100 : 0;
    };

    const completionPercentage = getCompletionPercentage();



        return (
        <Box sx={{ p: 4 }}>
            {/* Header with Progress */}
            <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 4
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            mr: 1
                        }}>
                            <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                        </Box>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                Specifications & Targets
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Define target values, acceptable ranges, and tolerance limits
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h5" sx={{ fontWeight: 500, color: specificationComplete ? theme.palette.success.main : theme.palette.warning.main }}>
                                {Math.round(completionPercentage)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Complete
                            </Typography>
                        </Box>
                    </Stack>
                    
                    <LinearProgress 
                        variant="determinate" 
                        value={completionPercentage} 
                        sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.grey[400], 0.3),
                            '& .MuiLinearProgress-bar': {
                                background: specificationComplete 
                                    ? `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`
                                    : `linear-gradient(90deg, ${theme.palette.warning.main}, ${alpha(theme.palette.warning.main, 0.8)})`
                            }
                        }} 
                    />
                </CardContent>
            </Card>
            
            <Grid container spacing={4}>
            
            {/* Test Type Context */}
            {formData.test_type && (
                <Grid item xs={12}>
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <ScienceIcon sx={{ color: theme.palette.primary.main }} />
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                        Test Type: {formData.test_type}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Unit: {formData.target_unit || 'Not specified'}
                                    </Typography>
                                </Box>
                                {formData.target_value && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<CalculateIcon />}
                                        onClick={autoCalculateSymmetricRange}
                                        sx={{ 
                                            ml: 'auto',
                                            fontWeight: 500,
                                            borderRadius: 3,
                                            border: `1px solid ${theme.palette.primary.main}`,
                                            color: theme.palette.primary.main,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                                border: `1px solid ${theme.palette.primary.main}`,
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                            }
                                        }}
                                    >
                                        Auto Calculate 5% Range
                                    </Button>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            )}

            {/* Validation Alerts */}
            {Object.keys(validationErrors).length > 0 && (
                <Grid item xs={12}>
                    <Alert 
                        severity={validationErrors.range || validationErrors.target_range ? "error" : "warning"}
                        sx={{ 
                            borderRadius: 2,
                            backgroundColor: (validationErrors.range || validationErrors.target_range)
                                ? alpha(theme.palette.error.main, 0.1)
                                : alpha(theme.palette.warning.main, 0.1),
                            border: `1px solid ${(validationErrors.range || validationErrors.target_range)
                                ? alpha(theme.palette.error.main, 0.3)
                                : alpha(theme.palette.warning.main, 0.3)}`,
                            '& .MuiAlert-icon': {
                                color: (validationErrors.range || validationErrors.target_range)
                                    ? theme.palette.error.main
                                    : theme.palette.warning.main
                            }
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ 
                            fontWeight: 500, 
                            mb: 1, 
                            color: (validationErrors.range || validationErrors.target_range)
                                ? theme.palette.error.main
                                : theme.palette.warning.main
                        }}>
                            Specification Issues:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {Object.values(validationErrors).map((error, index) => (
                                <li key={index}>
                                    <Typography variant="body2" sx={{ 
                                        color: (validationErrors.range || validationErrors.target_range)
                                            ? theme.palette.error.main
                                            : theme.palette.warning.main,
                                        fontWeight: 500 
                                    }}>{error}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Alert>
                </Grid>
            )}

            {/* Pass/Fail Specification - Simple Text Box */}
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Acceptance Criteria *"
                    value={formData.acceptance_criteria || ''}
                    onChange={(e) => setFormData(prev => ({...prev, acceptance_criteria: e.target.value}))}
                    placeholder="Define what constitutes a PASS vs FAIL result. Example: 'No visible lumps, uniform color, no separation, homogeneous appearance throughout sample'"
                    error={!!validationErrors.acceptance_criteria}
                    helperText={validationErrors.acceptance_criteria || "Describe specific criteria for pass/fail determination"}
                    required
                    InputLabelProps={{
                        sx: { fontWeight: 500, backgroundColor: 'white'}
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& textarea': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Target Unit"
                    value={formData.target_unit || ''}
                    onChange={(e) => handleTargetUnitChange(e.target.value)}
                    placeholder="e.g., pH, cP, mg/ml, %, g, ml"
                    helperText="Measurement unit for this test"
                    InputLabelProps={{
                        sx: { fontWeight: 500 }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                border: `1px solid ${theme.palette.grey[400]}`,
                                borderRadius: 3
                            },
                            '&:hover fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '&.Mui-focused fieldset': {
                                border: `1px solid ${theme.palette.primary.main}`
                            },
                            '& input': {
                                py: 2,
                                fontWeight: 500
                            },
                            backgroundColor: 'white',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                            }
                        }
                    }}
                />
            </Grid>
            
            </Grid>
        </Box>
    );
} 